import React from 'react';
import { useParams } from 'react-router-dom';
import { FormikProvider, useFormik } from 'formik';
import { plotBeingCreatedValidationSchema } from 'app/edozvola/cases/case-building-plot/model/case-building-plot-form.schema';
import { plotBeingCreatedInitial, PlotBeingCreatedModel } from 'app/edozvola/cases/case-building-plot/model/case-building-plot-form.model';
import { Button, Input, Modal } from 'edozvola-frontend-lib/dist/ui/components';
import { Col, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { Loader } from 'app/shared/loader/loader';
import { Label } from 'edozvola-frontend-lib/dist/ui';
import { PaginatedDropdown } from 'edozvola-frontend-lib/dist/ui/components/paginated-dropdown';
import { PARCEL_REGEX } from 'app/config/constants';
import { DkpLocationModel } from 'app/edozvola/cases/case-location/model/case-location.model';
import { CodebookModel } from 'app/shared/model/shared.model';
import {
  fetchPlotBeingCreatedQuery,
  savePlotBeingCreatedMutation,
} from 'app/edozvola/cases/case-building-plot/service/case-building-plot-form.queries';
import { fetchCadastralMunicipalitiesQuery } from 'app/edozvola/codebooks/codebooks.queries';

export interface ICaseBuildingPlotBeingCreatedForm {
  idCaseBuildingPlotBeingCreated: string | null;
  setIdCaseBuildingPlotBeingCreated: any;
  readOnly: boolean;
  dkpLocations: DkpLocationModel[];
}

const CaseBuildingPlotBeingCreatedForm = (props: ICaseBuildingPlotBeingCreatedForm) => {
  const { id: uuid } = useParams<{ id: string }>();

  const { data: cadastralMunicipalitiesOptions, isLoading: cadastralMunicipalityOptionsLoading } = fetchCadastralMunicipalitiesQuery();
  const { data: plotBeingCreated, isLoading: plotBeingCreatedLoading } = fetchPlotBeingCreatedQuery(props.idCaseBuildingPlotBeingCreated);
  const { mutate: savePlotBeingCreated, isPending: savingPlotBeingCreated} = savePlotBeingCreatedMutation(uuid, () => props.setIdCaseBuildingPlotBeingCreated(null))

  const formik = useFormik({
    validationSchema: plotBeingCreatedValidationSchema,
    validateOnMount: true,
    enableReinitialize: true,
    initialValues: plotBeingCreated || plotBeingCreatedInitial,
    onSubmit: (values: PlotBeingCreatedModel) => savePlotBeingCreated(values)
  })

  const closeModal = () => {
    props.setIdCaseBuildingPlotBeingCreated(null);
    formik.resetForm({values: plotBeingCreatedInitial});
  };

  const selectedMunicipalityInDkpLocations = idMunicipality => {
    return props.dkpLocations.map(dkpLocation => dkpLocation.cadastralMunicipality.length > 0 && dkpLocation.cadastralMunicipality[0].id).includes(idMunicipality)
  }

  const getDkpParcelsForMunicipality = idMunicipality => {
    return props.dkpLocations.filter(dkpLocation => dkpLocation.cadastralMunicipality.length > 0 && dkpLocation.cadastralMunicipality[0].id === idMunicipality).flatMap(dkpLocation => dkpLocation.parcels)
  }

  const cadastralMunicipalityChanged = (option: CodebookModel[]) => {
    if (option.length > 0 && selectedMunicipalityInDkpLocations(option[0].id)) {
      formik.setValues({
        ...formik.values,
        cadastralMunicipality: option,
        beingCreatedFrom: getDkpParcelsForMunicipality(option[0].id)
      })
    } else {
      formik.setValues({
        ...formik.values,
        cadastralMunicipality: option,
        beingCreatedFrom: []
      })
    }
  }

  const parcelChanged = event => {
    PARCEL_REGEX.test(event.target.value)
      ? formik.setFieldValue('plot', event.target.value)
      : event.preventDefault();
  };

  const removeBeingCreatedFromParcel = (idCadastralParcelToRemove) => {
    formik.setFieldValue('beingCreatedFrom', formik.values.beingCreatedFrom.filter(parcel => parcel.idDkpCadastralParcel !== idCadastralParcelToRemove))
  }

  return (
    <>
      <Modal scrollable size={'xl'} isOpen={props.idCaseBuildingPlotBeingCreated !== null}>
        {props.readOnly ? (
          <ModalHeader toggle={() => closeModal()}>Pregled građevne čestice</ModalHeader>
        ) : (
          <ModalHeader>{props.idCaseBuildingPlotBeingCreated === '' ? 'Unos građevne čestice' : 'Uređivanje građevne čestice'}</ModalHeader>
        )}
        <ModalBody>
          <FormikProvider value={formik}>
            {plotBeingCreatedLoading && <Loader />}
            {!plotBeingCreatedLoading && (
              <div className={'d-flex justify-content-between flex-column gap-3'}>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Katastarska općina'} />
                    <PaginatedDropdown
                      positionFixed
                      placeholder={!props?.readOnly && 'Odaberi katastarsku općinu'}
                      onItemSelect={cadastralMunicipalityChanged}
                      selected={formik.values?.cadastralMunicipality}
                      labelKey={'value'}
                      options={cadastralMunicipalityOptionsLoading ? [] : cadastralMunicipalitiesOptions}
                      clearButton
                      showErrorMessage
                      disabled={props.readOnly}
                      error={!props.readOnly && ((formik.errors?.cadastralMunicipality as string) ?? '')}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Građevna čestica'} />
                    <Input
                      value={formik.values.plot}
                      onChange={parcelChanged}
                      error={!props.readOnly && formik.values.cadastralMunicipality.length > 0 && (formik.errors.plot as string)}
                      disabled={props.readOnly || formik.values.cadastralMunicipality.length === 0}
                    />
                  </Col>
                </Row>
              </div>
            )}
            {formik.values.beingCreatedFrom.length > 0 && (
              <>
                <hr />
                <Label labelText={'Nastala iz'} />
                {formik.values.beingCreatedFrom.map(parcel => (
                  <>
                    <Row>
                      <Col md={6}>
                        <div className={'d-flex gap-3 flex-row'}>
                          <Input value={parcel.cadastralParcel} disabled />
                          <Button
                            prependIcon={'trash'}
                            outline
                            onClick={() => removeBeingCreatedFromParcel(parcel.idDkpCadastralParcel)}
                            className={'delete-button'}
                          />
                        </div>
                      </Col>
                    </Row>
                  </>
                ))}
              </>
            )}
          </FormikProvider>
        </ModalBody>
        <ModalFooter>
          {!props.readOnly && (
            <>
              <Button label={'Odustani'} outline size={'lg'} onClick={closeModal} />
              <Button
                label={'Spremi'}
                size={'lg'}
                disabled={!formik.dirty || !formik.isValid}
                isLoading={savingPlotBeingCreated}
                color={'info'}
                onClick={() => formik.handleSubmit()}
              />
            </>
          )}
        </ModalFooter>
      </Modal>
    </>
  );

}

export default CaseBuildingPlotBeingCreatedForm
